import {
  CostTable,
  LoadingIndicator,
  PaymentWidget,
  Snackbar,
  StripeOptions,
  whitelistedStatusCodes,
} from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useConfiguration } from '@foxtrotplatform/platform-micro-site-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/free-solid-svg-icons';
import { faOctagonExclamation } from '@fortawesome/pro-light-svg-icons';

import { SignUpStep } from '../../../common/provider/SignUpContext';
import {
  createRazorPayOrder,
  createStripeIntent,
  getPriceBreakdownForTier,
} from '../../../common/api/payment';
import { ApiErrorResponse, PriceBreakdown } from '../../../common/api/types';
import { useSignUp } from '../../../common/hooks/useSignUp';
import { isCountryIndia } from '../../signUpUtility';
import { ActionBar } from '../ActionBar';
import { createAccount } from '../../../common/api/sign-up';
import { CustomerSignupAppConfig } from '../../../index';
import { Header } from '../Header';

interface RazorpayOptions {
  keyId: string;
  orderId: string;
  customerName?: string;
}

interface PaymentContentProps {
  paymentOptions: StripeOptions | RazorpayOptions;
  gateway: 'razorpay' | 'stripe';
  onSuccessHandler: (id?: string) => Promise<void>;
  onSubmit?: () => Promise<void>;
  onSubscribe?: () => Promise<void>;
  isValid?: boolean;
  submitPayment?: boolean;
}

const pennyDropMessage = `
    We require a credit card in order to create your subscription. A
    temporary authorization charge for a small amount will be
    processed and fully refunded.
  `;

const PaymentContent = forwardRef<HTMLFormElement, PaymentContentProps>(
  (
    {
      paymentOptions,
      gateway,
      onSubmit,
      onSuccessHandler,
      isValid,
      onSubscribe,
    },
    ref
  ) => {
    const { formId, setErrorMessage } = useSignUp();
    const [productPricing, setProductPricing] = useState<PriceBreakdown[]>();

    useEffect(() => {
      if (formId) {
        const getProductPricing = async () => {
          try {
            const pricing = await getPriceBreakdownForTier(formId);
            if (pricing) {
              setProductPricing(pricing);
            }
          } catch (e: any) {
            if (whitelistedStatusCodes.includes(e.request.status)) {
              const errorResponse: ApiErrorResponse = JSON.parse(
                e.request.response
              );
              setErrorMessage(errorResponse.error.description);
            } else {
              setErrorMessage('Please go back to the form and try again');
            }
          }
        };
        getProductPricing();
      }
    }, [formId, setErrorMessage]);

    const onErrorHandler = useCallback(() => {
      setErrorMessage('There was an error processing your payment');
    }, [setErrorMessage]);

    const items = useMemo(() => {
      if (productPricing) {
        return getItemsFromProduct(productPricing);
      }
      return undefined;
    }, [productPricing]);

    const totals = useMemo(() => {
      if (productPricing) {
        return getTotalsFromProduct(productPricing);
      }
      return undefined;
    }, [productPricing]);

    const derivedRazorpayOptions = useMemo(() => {
      if (gateway === 'razorpay' && totals?.total.amount === 0) {
        return { ...paymentOptions, isCreditCardOnly: true };
      }
      return paymentOptions;
    }, [totals, paymentOptions, gateway]);

    const headerSubtitle = useMemo(() => {
      if (totals?.total?.amount === 0 && gateway === 'razorpay') {
        return 'Please review the subscription details below. The subscription price has been prorated and includes promotional discounts.';
      }
      return 'Please review the subscription and provide payment. The subscription price has been prorated and includes promotional discounts.';
    }, [totals, gateway]);

    if (
      !items ||
      !totals ||
      !paymentOptions ||
      (gateway === 'razorpay' && !derivedRazorpayOptions)
    ) {
      return (
        <div className="flex flex-col w-full h-full justify-center items-center">
          <LoadingIndicator />
        </div>
      );
    }

    return (
      <div className="flex flex-col h-[100vh] w-full content-between items-center">
        <Header subtitle={headerSubtitle} title="Complete Checkout" />
        <div className="flex-1 grid grid-cols-12 w-full px-6 xl:px-0 pb-8 font-opensans lg:overflow-auto mb-20 lg:mb-56">
          <div className="col-start-1 xl:col-start-2 col-span-10 flex flex-col gap-4">
            {productPricing &&
            productPricing.every((tier) => tier.isTrial) &&
            gateway === 'razorpay' ? (
              <div className="h-fit border rounded-lg p-5">
                <CostTable items={items} totals={totals} />
              </div>
            ) : (
              <>
                {totals.total.amount === 0 && (
                  <div>
                    <Snackbar
                      fontAwesomeIcon={faOctagonExclamation}
                      level="info"
                      message={pennyDropMessage}
                    />
                  </div>
                )}
                <PaymentWidget
                  gateway={gateway}
                  items={items}
                  totals={totals}
                  stripeOptions={paymentOptions as StripeOptions}
                  razorpayOptions={derivedRazorpayOptions as RazorpayOptions}
                  successHandler={onSuccessHandler}
                  errorHandler={onErrorHandler}
                  ref={ref}
                  subscribeHandler={onSubscribe}
                />
              </>
            )}
            {isValid && gateway !== 'stripe' && (
              <div className="w-full flex flex-row items-center gap-x-3 bg-success-100 rounded-[0.625rem] py-2.5 px-3 mt-6">
                <FontAwesomeIcon
                  icon={faCircleCheck}
                  className="text-success-800"
                />
                <span className="text-success-900 text-xs">
                  <span className="font-bold">Success! </span>Your payment has
                  been processed and you can now proceed.
                </span>
              </div>
            )}
          </div>
        </div>
        <ActionBar
          isValid={isValid || items.every((item) => item.isTrial)}
          onSubmit={onSubmit}
        />
      </div>
    );
  }
);

PaymentContent.displayName = PaymentContent.name;

const RazorpayPayment = () => {
  const { configuration } = useConfiguration();
  const { razorpayPublicKey } = configuration as CustomerSignupAppConfig;
  const { formId, setRequestId, setSignUpStep, setApiSubscriptionDetails } =
    useSignUp();
  const [razorpayOptions, setRazorpayOptions] = useState<any>();
  const [paymentFullfillment, setPaymentFullfillment] = useState<string>();
  const [paymentIsSuccessful, setPaymentIsSuccessful] =
    useState<boolean>(false);

  useEffect(() => {
    const getRazorPayOptions = async () => {
      const options = await createRazorPayOrder(formId as string);
      setRazorpayOptions({
        ...options,
        keyId: razorpayPublicKey,
      });
    };
    getRazorPayOptions();
  }, [formId, razorpayPublicKey]);

  const onSuccessHandler = useCallback(
    async (paymentId?: string) => {
      if (paymentId) {
        setPaymentFullfillment(paymentId);
      }
      setPaymentIsSuccessful(true);

      setSignUpStep(SignUpStep.paymentComplete);
    },
    [setPaymentIsSuccessful, setSignUpStep]
  );

  const onSubmit = useCallback(async () => {
    try {
      setSignUpStep(SignUpStep.settingUp);
      const { complete, requestId, apiDetails } = await createAccount(
        formId as string,
        paymentFullfillment as string
      );
      if (complete) {
        if (apiDetails) {
          setApiSubscriptionDetails(apiDetails);
        } else if (requestId) {
          setRequestId(requestId);
        }
      } else {
        setSignUpStep(SignUpStep.setupFailed);
      }
    } catch {
      setSignUpStep(SignUpStep.setupFailed);
    }
  }, [
    setSignUpStep,
    setRequestId,
    setApiSubscriptionDetails,
    formId,
    paymentFullfillment,
  ]);

  return (
    <PaymentContent
      paymentOptions={razorpayOptions}
      gateway="razorpay"
      onSubmit={onSubmit}
      onSuccessHandler={onSuccessHandler}
      isValid={paymentIsSuccessful}
    />
  );
};

const StripePayment = () => {
  const { configuration } = useConfiguration();
  const { stripePublicKey } = configuration as CustomerSignupAppConfig;
  const { formId, setSignUpStep, setRequestId, setApiSubscriptionDetails } =
    useSignUp();
  const [stripeOptions, setStripeOptions] = useState<any>();
  const htmlFormRef = useRef<HTMLFormElement>(null);
  const [paymentIsSuccessful, setPaymentIsSuccessful] =
    useState<boolean>(false);
  const [paymentFullfillment, setPaymentFullfillment] = useState<string>();
  const ref = useRef(false);
  useEffect(() => {
    if (!ref.current) {
      getStripeOptions();
    }
    return () => {
      ref.current = true;
    };
  }, [formId, stripePublicKey]);

  const getStripeOptions = async () => {
    const { client_secret: clientSecret, totals } = await createStripeIntent(
      formId as string
    );
    setStripeOptions({
      clientSecret,
      totals,
      showButton: false,
      publishableKey: stripePublicKey,
    });
  };

  const onSubmit = useCallback(async () => {
    try {
      setSignUpStep(SignUpStep.settingUp);
      const { complete, requestId, apiDetails } = await createAccount(
        formId as string,
        paymentFullfillment as string
      );
      if (complete) {
        if (apiDetails) {
          setApiSubscriptionDetails(apiDetails);
        } else if (requestId) {
          setRequestId(requestId);
        }
      } else {
        setSignUpStep(SignUpStep.setupFailed);
      }
    } catch (e) {
      setSignUpStep(SignUpStep.setupFailed);
    }
  }, [
    setSignUpStep,
    setRequestId,
    setApiSubscriptionDetails,
    formId,
    paymentFullfillment,
  ]);

  const onSubscribe = async () => {
    document.dispatchEvent(
      new Event('SubscrbedForStripe', {
        bubbles: true,
        cancelable: true,
      })
    );
  };

  const onSuccessHandler = useCallback(
    async (paymentId?: string) => {
      if (paymentId == 'true') {
        setPaymentIsSuccessful(true);
        setPaymentFullfillment(undefined);
      } else {
        setPaymentIsSuccessful(false);
      }
    },
    [setPaymentIsSuccessful]
  );

  return (
    <PaymentContent
      paymentOptions={stripeOptions}
      gateway="stripe"
      onSuccessHandler={onSuccessHandler}
      onSubmit={onSubscribe}
      ref={htmlFormRef}
      onSubscribe={onSubmit}
      isValid={paymentIsSuccessful}
    />
  );
};

export const PaymentComponent = () => {
  const { signUpCustomerCountry } = useSignUp();

  const countryIsIndia = useMemo(() => {
    if (signUpCustomerCountry) {
      return isCountryIndia(signUpCustomerCountry);
    }
    return false;
  }, [signUpCustomerCountry]);

  if (countryIsIndia) {
    return <RazorpayPayment />;
  }
  return <StripePayment />;
};

const getItemsFromProduct = (productPricing: PriceBreakdown[]) => {
  return productPricing?.map((product) => {
    return {
      id: product.tierId,
      name: `${product.label} ${product.tierName} ${
        product.tierName.toLocaleLowerCase() !== 'trial' ? 'Monthly' : ''
      } Plan`,
      isTrial: product.isTrial,
      originalPrice: {
        amount: product.originalPrice.amount || 0,
        currency: product.originalPrice.currency || 'USD',
        percision: product.originalPrice.precision || 2,
      },
      proratedPrice: {
        amount: product.proRatedPrice.amount || 0,
        currency: product.proRatedPrice.currency || 'USD',
        percision: product.proRatedPrice.precision || 2,
      },
      proratedDiscount: {
        amount: product.proRatedPriceDiscountAmount?.amount || 0,
        currency: product.proRatedPriceDiscountAmount?.currency || 'USD',
        percision: product.proRatedPriceDiscountAmount?.precision || 2,
      },
    };
  });
};

const getTotalsFromProduct = (productPricing: PriceBreakdown[]) => {
  const subtotal =
    productPricing?.reduce((a, b) => a + (b['dueBeforeTax'].amount || 0), 0) ||
    0;
  const taxArray = productPricing?.map((product) =>
    product.taxes.reduce((a, b) => a + (b['amount'].amount || 0), 0)
  );
  const taxTotal = taxArray?.reduce((a, b) => a + b) || 0;
  return {
    subtotal: {
      amount: subtotal,
      currency: productPricing[0].proRatedPriceAfterDiscount.currency,
      percision: productPricing[0].proRatedPriceAfterDiscount.precision,
    },
    tax: {
      amount: taxTotal,
      currency: productPricing[0].proRatedPriceAfterDiscount.currency,
      percision: productPricing[0].proRatedPriceAfterDiscount.precision,
    },
    total: {
      amount: subtotal + taxTotal,
      currency: productPricing[0].proRatedPriceAfterDiscount.currency,
      percision: productPricing[0].proRatedPriceAfterDiscount.precision,
    },
  };
};
